/**
 * Amazon API client functions for the frontend.
 */
import {
  type AmazonCallbackResponse,
  type AmazonProfile,
  type AmazonStatusResponse,
  amazonCallbackResponseSchema,
  amazonProfileSchema,
  type SpApiRegion,
  type SyncRun,
  type SyncStatusResponse,
  syncRunSchema,
  syncStatusResponseSchema,
} from "@adpilot/shared";
import { z } from "zod";
import { api } from "./api";

/**
 * Start an Amazon Ads grant. `region` picks the Amazon sign-in the seller goes
 * through; without it the grant signs in through North America and lists
 * every region's profiles (callers that predate region choice).
 */
export async function fetchAmazonConnectUrl(
  mode: "popup" | "redirect" = "redirect",
  region?: SpApiRegion,
): Promise<{ redirectUrl: string }> {
  const res = await api
    .get("auth/amazon/connect", { searchParams: { mode, ...(region ? { region } : {}) } })
    .json<{ success: true; data: { redirectUrl: string } }>();
  return res.data;
}

/**
 * Exchange an OAuth authorization code for Amazon Ads connection.
 * Called by the frontend callback page after Amazon LWA redirects back.
 * Returns a discriminated union:
 *   - { type: "connected", shop } — single profile, auto-connected
 *   - { type: "select_profile", selectionState, profiles } — multiple profiles, user must choose
 */
export async function exchangeAmazonCode(
  code: string,
  state: string,
): Promise<AmazonCallbackResponse> {
  const res = await api
    .post("auth/amazon/callback", { json: { code, state } })
    .json<{ success: true; data: unknown }>();
  return amazonCallbackResponseSchema.parse(res.data);
}

const amazonProfilesResponseSchema = z.object({ profiles: z.array(amazonProfileSchema) });

export async function fetchAmazonProfiles(state: string): Promise<{ profiles: AmazonProfile[] }> {
  const res = await api
    .get(`auth/amazon/profiles?state=${encodeURIComponent(state)}`)
    .json<{ success: true; data: unknown }>();
  return amazonProfilesResponseSchema.parse(res.data);
}

export async function fetchAmazonStatus(): Promise<AmazonStatusResponse> {
  const res = await api
    .get("auth/amazon/status")
    .json<{ success: true; data: AmazonStatusResponse }>();
  return res.data;
}

export async function selectAmazonProfiles(payload: {
  profileIds: string[];
  state: string;
}): Promise<{
  shops: Array<{ id: string; name: string }>;
  amazonAccounts: Array<{
    id: string;
    profileId: string;
    marketplaceName: string;
    status: string;
  }>;
}> {
  const res = await api.post("auth/amazon/select-profile", { json: payload }).json<{
    success: true;
    data: {
      shops: Array<{ id: string; name: string }>;
      amazonAccounts: Array<{
        id: string;
        profileId: string;
        marketplaceName: string;
        status: string;
      }>;
    };
  }>();
  return res.data;
}

// ─── Account resources (ADP-26) ─────────────────────────────────

export interface AccountShop {
  shopId: string;
  shopName: string;
  /** ADP-66: the Shop's tenant Workspace — the ShopSwitcher scopes to the active one. */
  workspaceId: string;
  role: "owner" | "admin" | "member";
  accountId: string;
  profileId: string;
  adsTimezone: string | null;
  /** Amazon seller merchant / vendor entity id (e.g. "A3L1NLZ3G1T6C4"). */
  merchantId: string | null;
  accountType: "seller" | "vendor" | null;
  marketplaceId: string;
  marketplaceName: string;
  status: "connected" | "token_invalid" | "disconnected";
  isActive: boolean;
  deactivatedAt: string | null;
  connectedAt: string;
  lastSyncAt: string | null;
}

export interface AccountConnection {
  id: string;
  label: string | null;
  status: "connected" | "token_invalid" | "revoked";
  tokenExpiresAt: string;
  connectedAt: string;
  shops: AccountShop[];
}

/** Keep connection-level actions and status inside the active Workspace. */
export function scopeConnectionsToWorkspace(
  connections: AccountConnection[],
  workspaceId: string | null | undefined,
): AccountConnection[] {
  if (!workspaceId) return [];
  return connections.flatMap((connection) => {
    const shops = connection.shops.filter((shop) => shop.workspaceId === workspaceId);
    return shops.length > 0 ? [{ ...connection, shops }] : [];
  });
}

export async function fetchAmazonAccounts(): Promise<{ connections: AccountConnection[] }> {
  const res = await api
    .get("auth/amazon/accounts")
    .json<{ success: true; data: { connections: AccountConnection[] } }>();
  return res.data;
}

export async function reauthorizeAccount(shopId: string): Promise<{ redirectUrl: string }> {
  const res = await api
    .post(`auth/amazon/accounts/${shopId}/reauthorize`)
    .json<{ success: true; data: { redirectUrl: string } }>();
  return res.data;
}

export async function disconnectShopAccount(shopId: string): Promise<void> {
  await api.delete(`auth/amazon/accounts/${shopId}`).json();
}

export async function setShopActivation(shopId: string, isActive: boolean): Promise<void> {
  await api.patch(`auth/amazon/accounts/${shopId}/activation`, { json: { isActive } }).json();
}

/**
 * Permanently delete Shops and all their AdPraxis data; Amazon is never touched.
 * The server clears the history in one transaction, so a large store can take
 * minutes — matching the proxy's 300s window for this endpoint.
 */
export async function deleteShops(shopIds: readonly string[]): Promise<void> {
  await api.post("auth/amazon/shops/bulk-delete", { json: { shopIds }, timeout: 300_000 }).json();
}

/** AdPraxis-only display name for every Shop in the connection; never written back to Amazon. */
export async function renameConnectionShops(connectionId: string, name: string): Promise<void> {
  await api.patch(`auth/amazon/connections/${connectionId}/name`, { json: { name } }).json();
}

export async function disconnectConnection(connectionId: string): Promise<void> {
  await api.delete(`auth/amazon/connections/${connectionId}`).json();
}

export async function syncShopAccount(shopId: string): Promise<{ batchId: string }> {
  const res = await api
    .post(`auth/amazon/accounts/${shopId}/sync`)
    .json<{ success: true; data: { batchId: string } }>();
  return res.data;
}

export async function syncAmazonConnection(
  connectionId: string,
): Promise<{ shopCount: number; batchIds: string[] }> {
  const res = await api
    .post(`auth/amazon/connections/${connectionId}/sync`)
    .json<{ success: true; data: { shopCount: number; batchIds: string[] } }>();
  return res.data;
}

export async function retrySyncBatch(batchId: string): Promise<{ batchId: string }> {
  const res = await api
    .post(`sync/batches/${batchId}/retry`)
    .json<{ success: true; data: { batchId: string } }>();
  return res.data;
}

export interface RetryFailedSyncsResult {
  batchIds: string[];
  retriedBatchCount: number;
  failedBatchCount: number;
}

export async function retryWorkspaceSync(workspaceId: string): Promise<RetryFailedSyncsResult> {
  const res = await api
    .post(`sync/workspaces/${workspaceId}/retry`)
    .json<{ success: true; data: RetryFailedSyncsResult }>();
  return res.data;
}

export async function retryAmazonConnectionSync(
  connectionId: string,
): Promise<RetryFailedSyncsResult> {
  const res = await api
    .post(`sync/connections/${connectionId}/retry`)
    .json<{ success: true; data: RetryFailedSyncsResult }>();
  return res.data;
}

export async function fetchShopSyncStatus(shopId: string): Promise<SyncStatusResponse> {
  const res = await api
    .get("sync/status", { headers: { "X-Shop-Id": shopId } })
    .json<{ success: true; data: unknown }>();
  // Validate against the shared schema so API/web SyncRun types can't drift silently.
  return syncStatusResponseSchema.parse(res.data);
}

export async function fetchShopSyncRun(shopId: string, runId: string): Promise<SyncRun> {
  const res = await api
    .get(`sync/shops/${encodeURIComponent(shopId)}/runs/${encodeURIComponent(runId)}`)
    .json<{ success: true; data: unknown }>();
  return syncRunSchema.parse(res.data);
}

export async function triggerSync(): Promise<void> {
  await api.post("sync/trigger").json();
}

export interface McpSkillFile {
  filename: string;
  path: string;
  content: string;
}

export async function fetchMcpSkills(name = "all"): Promise<{ files: McpSkillFile[] }> {
  const res = await api
    .get(`auth/amazon/mcp-skills?name=${encodeURIComponent(name)}`)
    .json<{ success: true; data: { files: McpSkillFile[] } }>();
  return res.data;
}
